/**
 * Canonical listing reference-id generator.
 *
 * A reference id is an **opaque, stable identifier** that suppliers see in
 * email. It must never encode provenance: that lives in
 * `listings.creation_source` (+ `copied_from_listing_id` for duplicates).
 *
 * History / why this exists:
 *  - The duplicate action used to append `-copy-<suffix>` to the *source*
 *    listing's id, which nested up to four levels deep and made every copy
 *    resolve to a different, real listing when the ingest pipeline matched the
 *    `CF-` head. That is a data-integrity bug, not a cosmetic one.
 *  - `MANUAL-` / `DRAFT-` prefixes duplicated (and drifted from) column state:
 *    most `DRAFT-` rows are no longer drafts.
 *
 * Format: `CF-<5 base36 time chars><3 random chars>` — 8 chars after the
 * prefix, which is what the ingest matcher (`CF-[A-Z0-9]{5,10}`) expects.
 * Legacy ids are left untouched; we simply stop minting new shapes.
 */

const ALPHABET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
/** 36^5 — the time component wraps roughly every 16.8 hours. */
const TIME_MODULO = 60_466_176;

function randomChars(n: number): string {
  let out = "";
  const bytes = new Uint8Array(n);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < n; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  for (let i = 0; i < n; i++) out += ALPHABET[bytes[i] % ALPHABET.length];
  return out;
}

export function generateReferenceId(now = Date.now()): string {
  const time = (now % TIME_MODULO).toString(36).toUpperCase().padStart(5, "0");
  return `CF-${time}${randomChars(3)}`;
}

export type ListingCreationSource = "email" | "manual" | "copy" | "draft" | "import" | "api";
